/**
 * The handful of icons the plan screens use, drawn inline.
 *
 * Inline rather than a library because there are a dozen of them, they all share one
 * stroke weight, and a dependency for twelve paths is a dependency for the sake of it.
 * Each is a 16-unit box on a 1.75 stroke, so they sit on a button at any size and read
 * as one set. Every icon is decorative: the button carries the label.
 */
import type { SVGProps } from 'react'

type IconProps = SVGProps<SVGSVGElement> & { size?: number }

function Icon({ size = 16, children, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...rest}
    >
      {children}
    </svg>
  )
}

export const AddIcon = (p: IconProps) => (
  <Icon {...p}><path d="M8 3v10M3 8h10" /></Icon>
)
export const IndentIcon = (p: IconProps) => (
  <Icon {...p}><path d="M9 4h5M9 8h5M9 12h5M2 5.5 4.5 8 2 10.5" /></Icon>
)
export const OutdentIcon = (p: IconProps) => (
  <Icon {...p}><path d="M9 4h5M9 8h5M9 12h5M4.5 5.5 2 8l2.5 2.5" /></Icon>
)
export const UpIcon = (p: IconProps) => (
  <Icon {...p}><path d="M8 13V3M4 7l4-4 4 4" /></Icon>
)
export const DownIcon = (p: IconProps) => (
  <Icon {...p}><path d="M8 3v10M4 9l4 4 4-4" /></Icon>
)
export const RulesIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="2" y="3" width="12" height="11" rx="1.5" />
    <path d="M2 6.5h12M5.5 2v2.5M10.5 2v2.5M6 10.5l1.5 1.5L10 9" />
  </Icon>
)
export const TrashIcon = (p: IconProps) => (
  <Icon {...p}><path d="M3 4.5h10M6 4.5V3h4v1.5M4.5 4.5l.7 8.5h5.6l.7-8.5M6.8 7v4M9.2 7v4" /></Icon>
)
export const UndoIcon = (p: IconProps) => (
  <Icon {...p}><path d="M3.5 6.5h6a3 3 0 0 1 0 6H6M3.5 6.5 6 4M3.5 6.5 6 9" /></Icon>
)
export const ZoomInIcon = (p: IconProps) => (
  <Icon {...p}><circle cx="7" cy="7" r="4.5" /><path d="M13.5 13.5 10.3 10.3M7 5v4M5 7h4" /></Icon>
)
export const ZoomOutIcon = (p: IconProps) => (
  <Icon {...p}><circle cx="7" cy="7" r="4.5" /><path d="M13.5 13.5 10.3 10.3M5 7h4" /></Icon>
)
export const CriticalIcon = (p: IconProps) => (
  <Icon {...p}><path d="M2 12h5l2-8 2 8h3" /></Icon>
)
export const TightenIcon = (p: IconProps) => (
  <Icon {...p}><path d="M2 8h4M10 8h4M6 5.5 8 8l-2 2.5M10 5.5 8 8l2 2.5" /></Icon>
)
export const ColumnsIcon = (p: IconProps) => (
  <Icon {...p}><rect x="2" y="3" width="12" height="10" rx="1.5" /><path d="M6 3v10M10 3v10" /></Icon>
)
export const PrintIcon = (p: IconProps) => (
  <Icon {...p}><path d="M5 6V2.5h6V6M3.5 6h9a1.5 1.5 0 0 1 1.5 1.5V11h-2.5M2 11V7.5A1.5 1.5 0 0 1 3.5 6M4.5 9.5h7v4h-7z" /></Icon>
)
export const ExportIcon = (p: IconProps) => (
  <Icon {...p}><path d="M8 10V2.5M5 5.5 8 2.5l3 3M3 9.5v3a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-3" /></Icon>
)
export const ImportIcon = (p: IconProps) => (
  <Icon {...p}><path d="M8 2.5V10M5 7l3 3 3-3M3 9.5v3a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-3" /></Icon>
)
export const DeckIcon = (p: IconProps) => (
  <Icon {...p}><rect x="2" y="2.5" width="12" height="8.5" rx="1.5" /><path d="M8 11v2.5M5.5 13.5h5M5 8.5V6.5M8 8.5V5M11 8.5V7.5" /></Icon>
)
export const LinkIcon = (p: IconProps) => (
  <Icon {...p}><path d="M6.5 9.5 9.5 6.5M7 4.5l1-1a2.5 2.5 0 0 1 3.5 3.5l-1 1M9 11.5l-1 1a2.5 2.5 0 0 1-3.5-3.5l1-1" /></Icon>
)
export const MilestoneIcon = (p: IconProps) => (
  <Icon {...p}><path d="M8 2.5 13.5 8 8 13.5 2.5 8z" /></Icon>
)
export const ChevronDownIcon = (p: IconProps) => (
  <Icon {...p}><path d="M4 6.5 8 10l4-3.5" /></Icon>
)
export const FitIcon = (p: IconProps) => (
  <Icon {...p}><path d="M2 6V3.5A1.5 1.5 0 0 1 3.5 2H6M10 2h2.5A1.5 1.5 0 0 1 14 3.5V6M14 10v2.5a1.5 1.5 0 0 1-1.5 1.5H10M6 14H3.5A1.5 1.5 0 0 1 2 12.5V10M5 8h6" /></Icon>
)
export const TodayIcon = (p: IconProps) => (
  <Icon {...p}><rect x="2" y="3" width="12" height="11" rx="1.5" /><path d="M2 6.5h12M5.5 2v2.5M10.5 2v2.5" /><circle cx="8" cy="10" r="1.4" fill="currentColor" stroke="none" /></Icon>
)
export const RedoIcon = (p: IconProps) => (
  <Icon {...p}><path d="M12.5 6.5h-6a3 3 0 0 0 0 6H10M12.5 6.5 10 4M12.5 6.5 10 9" /></Icon>
)
export const UnlinkIcon = (p: IconProps) => (
  <Icon {...p}><path d="M7 4.5l1-1a2.5 2.5 0 0 1 3.5 3.5l-1 1M9 11.5l-1 1a2.5 2.5 0 0 1-3.5-3.5l1-1M3 3l10 10" /></Icon>
)
export const LockIcon = (p: IconProps) => (
  <Icon {...p}><rect x="3.5" y="7" width="9" height="6.5" rx="1.5" /><path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" /></Icon>
)
export const FilterIcon = (p: IconProps) => (
  <Icon {...p}><path d="M2.5 3.5h11L9.5 8.5v4l-3 1.5v-5.5z" /></Icon>
)
export const DensityIcon = (p: IconProps) => (
  <Icon {...p}><path d="M2.5 4h11M2.5 8h11M2.5 12h11" /></Icon>
)
export const InsertAboveIcon = (p: IconProps) => (
  <Icon {...p}><path d="M2.5 11.5h11M2.5 14h11M8 2v6M5.5 4.5 8 2l2.5 2.5" /></Icon>
)
export const InsertBelowIcon = (p: IconProps) => (
  <Icon {...p}><path d="M2.5 2h11M2.5 4.5h11M8 8v6M5.5 11.5 8 14l2.5-2.5" /></Icon>
)
export const TickIcon = (p: IconProps) => (
  <Icon {...p}><path d="M3 8.5l3.2 3L13 4.5" /></Icon>
)
export const OpenIcon = (p: IconProps) => (
  <Icon {...p}><path d="M6.5 3.5h-3a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-3M9 2.5h4.5V7M13.5 2.5 7.5 8.5" /></Icon>
)
export const PaletteIcon = (p: IconProps) => (
  <Icon {...p}><path d="M8 2.5a5.5 5.5 0 1 0 0 11h1a1.5 1.5 0 0 0 0-3H8.5a1 1 0 0 1 0-2H10a3.5 3.5 0 0 0 0-7z" /><circle cx="5.5" cy="7" r=".9" fill="currentColor" stroke="none" /><circle cx="8" cy="5" r=".9" fill="currentColor" stroke="none" /><circle cx="10.5" cy="7" r=".9" fill="currentColor" stroke="none" /></Icon>
)
